import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { generatePath } from 'react-router-dom'

import {
  PreSaleDetailQuery,
  PreSaleDetailQueryVariables,
  SendVerificationSms,
  SendVerificationSmsMutation,
  SendVerificationSmsMutationVariables,
  SmsVerificationQuery,
  SmsVerificationQueryVariables,
  UserDeliveryDetailFragmentDoc,
  VerifySmsCodeMutation,
  VerifySmsCodeMutationVariables,
} from '@/clients/utoniq-core/schema'
import { ApplyPreSaleLotteryValue } from '@/components/pages/LotteryDetail/template/LotteryDetailForm'
import { needToRegisterUserAddress } from '@/components/pages/LotteryDetail/viewModel'
import { useMessage, useRouter, useTranslation } from '@/core'
import { makeViewModel } from '@/core/architecture'
import {
  getPrefrenceObject,
  getUserInfoRegisterRedirect,
  setUserInfoRegisterRedirect,
} from '@/core/native/storage'
import { useRootAuth } from '@/core/rootAuth'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { router, routerPages } = useRouter()
  const rootAuthInfo = useRootAuth()
  const { showMessage } = useMessage()
  const { t } = useTranslation('smsVerification')
  const { data, refetch } = useQuery<
    SmsVerificationQuery,
    SmsVerificationQueryVariables
  >(QUERY)

  const [getPreSaleDetail] = useLazyQuery<
    PreSaleDetailQuery,
    PreSaleDetailQueryVariables
  >(PRE_SALE)

  const [verifySMSCode, { loading: verifying }] = useMutation<
    VerifySmsCodeMutation,
    VerifySmsCodeMutationVariables
  >(VERIFY_SMS_CODE)

  const [sendVerificationSMS] = useMutation<
    SendVerificationSmsMutation,
    SendVerificationSmsMutationVariables
  >(SendVerificationSms)

  return {
    state: {
      loading: verifying,
      phoneNumber: data?.user?.formattedPhoneNumber ?? null,
    },
    actions: {
      async onSubmit({ code }) {
        await verifySMSCode({ variables: { code } })
        await refetch()
        showMessage(t('verificationCodeSuccess'), 'success')

        const value = await getPrefrenceObject<
          ApplyPreSaleLotteryValue & { isFirstComeFirstServed?: boolean }
        >('preSaleLotteryApplicationInput')
        if (value?.preSaleId) {
          const preSale = await getPreSaleDetail({
            variables: {
              id: value.preSaleId,
              // クエリパラメータで取得したROOT認証情報を付与する UT-15288
              clientId: rootAuthInfo.clientId,
              token: rootAuthInfo.token,
            },
          })
          const personalInformationRequirements = value?.isFirstComeFirstServed
            ? preSale.data?.preSale?.firstComeFirstServed
                ?.personalInformationRequirements
            : preSale.data?.preSale.lottery?.personalInformationRequirements
          if (
            needToRegisterUserAddress(
              personalInformationRequirements ?? [],
              data?.user
            )
          ) {
            return router.replace(
              generatePath(routerPages.LotteryDeliveryInfo, {
                preSaleId: value.preSaleId,
              })
            )
          }
          router.goBack()
          return router.replace(
            generatePath(routerPages.LotteryConfirmation, {
              preSaleId: value.preSaleId,
            })
          )
        }

        const userInfoRegisterRedirect = await getUserInfoRegisterRedirect()
        if (userInfoRegisterRedirect) {
          await setUserInfoRegisterRedirect({
            ...userInfoRegisterRedirect,
            phoneNumber: true,
          })
        }

        const redirectPath = userInfoRegisterRedirect.address
          ? routerPages.StoreDeliveryInfo
          : userInfoRegisterRedirect.direction
        if (!redirectPath) {
          router.goBack()
          return
        }
        router.push(redirectPath)
      },
      async onResend() {
        await sendVerificationSMS()
      },
    },
  }
})

const QUERY = gql`
  query SMSVerification {
    user {
      ...UserDeliveryDetail
    }
  }
  ${UserDeliveryDetailFragmentDoc}
`

const PRE_SALE = gql`
  query preSaleDetail($id: ID!, $clientId: String, $token: String) {
    preSale(id: $id, clientId: $clientId, token: $token) {
      id
      lottery {
        personalInformationRequirements
      }
      firstComeFirstServed {
        personalInformationRequirements
      }
    }
  }
`

const VERIFY_SMS_CODE = gql`
  mutation VerifySMSCode($code: String!) {
    verifySMSCode(code: $code) {
      verified
    }
  }
`
